import React, { useState } from 'react';
import {
  Box,
  Heading,
  Flex,
  Button,
  Select,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  useToast,
  Card,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiDownload, FiTrendingUp, FiUsers, FiMessageSquare } from 'react-icons/fi';

const AnalyticsReports = () => {
  const [dateRange, setDateRange] = useState('7days');
  const [reportType, setReportType] = useState('all');
  const toast = useToast();

  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('gray.700', 'white');
  const hoverBg = useColorModeValue('gray.50', 'gray.600');
  const pageBg = useColorModeValue('gray.50', 'gray.800');
  // Sample analytics data
  const analyticsData = {
    totalTickets: 1250,
    resolvedTickets: 980,
    activeAgents: 15,
    averageResponseTime: '2.5 hours',
    ticketsByCategory: [
      { category: 'Technical', count: 450 },
      { category: 'Billing', count: 300 },
      { category: 'General', count: 500 },
    ],
    ticketsByStatus: [
      { status: 'Open', count: 150 },
      { status: 'In Progress', count: 120 },
      { status: 'Resolved', count: 980 },
    ],
  };

  const handleExportCSV = (type) => {
    // Convert data to CSV format based on type
    let headers = [];
    let data = [];

    switch (type) {
      case 'tickets':
        headers = ['Category', 'Count', 'Percentage'];
        data = analyticsData.ticketsByCategory.map(item => [
          item.category,
          item.count,
          `${((item.count / analyticsData.totalTickets) * 100).toFixed(2)}%`
        ]);
        break;
      case 'status':
        headers = ['Status', 'Count', 'Percentage'];
        data = analyticsData.ticketsByStatus.map(item => [
          item.status,
          item.count,
          `${((item.count / analyticsData.totalTickets) * 100).toFixed(2)}%`
        ]);
        break;
      default:
        headers = ['Metric', 'Value'];
        data = [
          ['Total Tickets', analyticsData.totalTickets],
          ['Resolved Tickets', analyticsData.resolvedTickets],
          ['Active Agents', analyticsData.activeAgents],
          ['Average Response Time', analyticsData.averageResponseTime],
        ];
    }

    const csvContent = [
      headers.join(','),
      ...data.map(row => row.join(','))
    ].join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `analytics_report_${type}_${dateRange}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: 'Export Successful',
      description: `Analytics report has been exported to CSV`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <Box p={5}>
      <Flex justify="space-between" align="center" mb={5}>
        <Heading size="lg">Analytics Reports</Heading>
        <HStack spacing={4}>
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            w="150px"
          >
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="year">This Year</option>
          </Select>
          <Button
            leftIcon={<FiDownload />}
            colorScheme="blue"
            onClick={() => handleExportCSV(reportType)}
          >
            Export Report
          </Button>
        </HStack>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={5} mb={5}>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Total Tickets</StatLabel>
              <StatNumber>{analyticsData.totalTickets}</StatNumber>
              <StatHelpText>
                <FiTrendingUp /> +12% from last period
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Resolved Tickets</StatLabel>
              <StatNumber>{analyticsData.resolvedTickets}</StatNumber>
              <StatHelpText>
                <FiTrendingUp /> +8% from last period
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Active Agents</StatLabel>
              <StatNumber>{analyticsData.activeAgents}</StatNumber>
              <StatHelpText>
                <FiUsers /> Currently Online
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Avg Response Time</StatLabel>
              <StatNumber>{analyticsData.averageResponseTime}</StatNumber>
              <StatHelpText>
                <FiMessageSquare /> Response Time
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Box bg={useColorModeValue('gray.100', 'gray.700')} p={5} borderRadius="lg" boxShadow="sm" mb={5}>
        <Heading size="md" mb={4}>Tickets by Category</Heading>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Category</Th>
              <Th>Count</Th>
              <Th>Percentage</Th>
            </Tr>
          </Thead>
          <Tbody>
            {analyticsData.ticketsByCategory.map((item, index) => (
              <Tr key={index}>
                <Td>{item.category}</Td>
                <Td>{item.count}</Td>
                <Td>{((item.count / analyticsData.totalTickets) * 100).toFixed(2)}%</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Box bg={useColorModeValue('gray.100', 'gray.700')} p={5} borderRadius="lg" boxShadow="sm">
        <Heading size="md" mb={4}>Tickets by Status</Heading>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Status</Th>
              <Th>Count</Th>
              <Th>Percentage</Th>
            </Tr>
          </Thead>
          <Tbody>
            {analyticsData.ticketsByStatus.map((item, index) => (
              <Tr key={index}>
                <Td>{item.status}</Td>
                <Td>{item.count}</Td>
                <Td>{((item.count / analyticsData.totalTickets) * 100).toFixed(2)}%</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default AnalyticsReports; 